import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSpinner, FaGoogle } from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';
import { shouldRecordPunchingTime } from '../../../utils/punchingTimeUtils';
import './LogIn.css';
import logo from '../../../assets/company-logos/Wyenfosbills_logo.png';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState((localStorage.getItem('role') || '').toLowerCase());
  const [dept, setDepartment] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showButterfly, setShowButterfly] = useState(false);
  const navigate = useNavigate();
  const { login, signInWithGoogle, userProfile, setUserProfile } = useAuth();
  const { email, password } = formData;

  useEffect(() => {
    const emailInput = document.getElementById('email');
    if (emailInput) {
      emailInput.focus();
    }
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = () => {
    const newErrors = [];
    if (!email.trim()) {
      newErrors.push({ msg: 'Email is required' });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.push({ msg: 'Please enter a valid email address' });
    }
    if (!password) {
      newErrors.push({ msg: 'Password is required' });
    } else if (password.length < 6) {
      newErrors.push({ msg: 'Password must be at least 6 characters' });
    }
    if (!role) {
      newErrors.push({ msg: 'Please select a role' });
    }
    if (role === 'admin' && !dept) {
      newErrors.push({ msg: 'Please select a department for Admin login' });
    }
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Debug function to check user in Firestore
  const debugUserInFirestore = async (uid) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/${uid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        console.log('🔍 User data from API:', userData);
        return userData;
      } else {
        console.log('❌ User not found in API:', response.status);
        return null;
      }
    } catch (error) {
      console.log('❌ Error fetching user:', error);
      return null;
    }
  };

  // Debug function to check login state
  window.debugLoginState = () => {
    console.log('🔍 Current Login State:', {
      role: role,
      dept: dept,
      normalizedRole: (role || '').toLowerCase().replace(/[ _]/g, ''),
      normalizedDept: (dept || '').toLowerCase().replace(/[ _]+/g, '-').trim(),
      localStorage: {
        role: localStorage.getItem('role'),
        dept: localStorage.getItem('dept'),
        email: localStorage.getItem('email')
      }
    });
  };

  // Debug function to check current user profile and routing
  window.debugUserProfile = async () => {
    console.log('🔍 Current User Profile Debug:', {
      localStorage: {
        role: localStorage.getItem('role'),
        dept: localStorage.getItem('dept'),
        email: localStorage.getItem('email')
      },
      currentPath: window.location.pathname
    });
    
    // Test route selection logic
    const normalizedRole = localStorage.getItem('role')?.toLowerCase().replace(/[ _]/g, '') || '';
    const normalizedDept = localStorage.getItem('dept')?.toLowerCase().replace(/[ _]+/g, '-').trim() || '';
    
    let targetRoute = '/dashboard';
    if (normalizedRole === 'superadmin' || normalizedRole === 'super_admin') {
      targetRoute = '/super-admin';
    } else if (normalizedRole === 'admin' || normalizedRole === 'purchaseadmin' || normalizedRole === 'purchase_admin') {
      switch (normalizedDept) {
        case 'accounts':
          targetRoute = '/admin';
          break;
        case 'hr':
          targetRoute = '/hr-admin';
          break;
        case 'marketing':
          targetRoute = '/marketing-admin';
          break;
        case 'digital-marketing':
          targetRoute = '/digital-marketing-admin';
          break;
        case 'purchase':
        case 'purchasing':
          targetRoute = '/purchasing-admin';
          break;
        default:
          targetRoute = '/dashboard';
      }
    }
    
    console.log('🔍 Expected Route:', targetRoute);
    console.log('🔍 Current Route:', window.location.pathname);
  };

  // Manual redirect function for testing
  window.redirectToPurchasingAdmin = () => {
    console.log('🔍 Manually redirecting to purchasing admin...');
    window.location.href = '/purchasing-admin';
  };

  // Global function to create a user document (for debugging)
  window.createUserDocument = async (uid, email, role = 'staff', department = '') => {
    try {
      const userData = {
        uid: uid,
        email: email,
        name: email.split('@')[0],
        role: role,
        department: department,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        isActive: true
      };
      
      console.log('🔄 Creating user document:', userData);
      
      // Try to create via Firebase directly
      const { db } = await import('../../../firebase');
      const { doc, setDoc } = await import('firebase/firestore');
      
      await setDoc(doc(db, 'users', uid), userData);
      console.log('✅ User document created successfully in Firestore');
      return userData;
    } catch (error) {
      console.error('❌ Error creating user document:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Add this at the top of your handleSubmit function for debugging
    localStorage.removeItem('role');
    localStorage.removeItem('dept');
    localStorage.removeItem('token');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors([]);
    setSuccessMessage('');

    try {
      console.log('Trying login with:', email, password);
      const result = await login(email, password);
      
      // Initialize variables at the top
      let normalizedRole = (role || '').toLowerCase().replace(/[ _]/g, '');
      let normalizedDept = (dept || '')
        .toLowerCase()
        .replace(/[ _]+/g, '-')
        .trim();
      
      console.log('🔍 Login Debug - Initial values:', {
        role: role,
        dept: dept,
        normalizedRole: normalizedRole,
        normalizedDept: normalizedDept
      });
      
      // Debug: Check user in Firestore
      const userData = await debugUserInFirestore(result.user.uid);
      
      // Store the current user profile to use throughout this function
      let currentUserProfile = userProfile;
      
      // Update userProfile with the fetched data if available
      if (userData && userData.success && userData.data) {
        console.log('🔄 Updating userProfile with fetched data:', userData.data);
        setUserProfile(userData.data);
        // Update the current profile for this function
        currentUserProfile = userData.data;
        
        // Debug: Log the user profile after login
        console.log('🔍 Login successful, user profile:', currentUserProfile);
        console.log('🔍 User profile role:', currentUserProfile?.role);
        console.log('🔍 Selected role:', role);
        
        // Use the updated profile for role validation
        if (currentUserProfile && currentUserProfile.role) {
          const userRole = currentUserProfile.role?.toLowerCase().replace(/[ _]/g, '');
          
          console.log('🔍 User Profile Role Debug:', {
            originalRole: currentUserProfile.role,
            userRole: userRole,
            normalizedRole: normalizedRole,
            department: currentUserProfile.department
          });
          
          if (userRole === 'super_admin' || userRole === 'superadmin') {
            normalizedRole = userRole;
            normalizedDept = currentUserProfile.department?.toLowerCase().replace(/[ _]+/g, '-').trim() || '';
          } else {
            // For purchase admin, use the profile role and department
            normalizedRole = userRole;
            normalizedDept = currentUserProfile.department?.toLowerCase().replace(/[ _]+/g, '-').trim() || '';
            console.log('🔍 Updated from profile:', { normalizedRole, normalizedDept });
          }
        } else {
          // If no user profile or role found, show a helpful error
          console.warn('⚠️ No user profile or role found for user:', result.user.uid);
          console.log('🔍 User data from API:', userData);
          
          // Show a more helpful error message
          throw new Error(`User account not found in the system. Please contact your administrator to set up your account with role: ${normalizedRole}`);
        }
      } else {
        // Fallback to the original userProfile from context
        console.log('🔍 Using userProfile from context:', currentUserProfile);
        console.log('🔍 User profile role:', currentUserProfile?.role);
        console.log('🔍 Selected role:', role);
        
        if (currentUserProfile && currentUserProfile.role) {
          const userRole = currentUserProfile.role?.toLowerCase().replace(/[ _]/g, '');
          
          if (userRole === 'super_admin' || userRole === 'superadmin') {
            normalizedRole = userRole;
            normalizedDept = currentUserProfile.department?.toLowerCase().replace(/[ _]+/g, '-').trim() || '';
          } else if (userRole !== normalizedRole) {
            throw new Error(`Invalid role for this user. Your account role is: ${currentUserProfile.role}`);
          }
        } else {
          console.warn('⚠️ No user profile or role found for user:', result.user.uid);
          console.log('🔍 User data from API:', userData);
          
          throw new Error(`User account not found in the system. Please contact your administrator to set up your account with role: ${normalizedRole}`);
        }
      }
      
      // Use currentUserProfile instead of userProfile for department check
      if (normalizedRole === 'admin' || normalizedRole === 'purchaseadmin' || normalizedRole === 'purchase_admin' || (currentUserProfile?.role?.toLowerCase().replace(/[ _]/g, '') === 'super_admin' && normalizedRole === 'admin')) {
        const userDept = (currentUserProfile.department || '')
          .toLowerCase()
          .replace(/[ _]+/g, '-')
          .trim();
        const departmentMapping = {
          'purchase': 'purchase',
          'purchasing': 'purchase',
          'Purchase': 'purchase',
          'Purchasing': 'purchase'
        };
        const normalizedUserDept = departmentMapping[userDept] || userDept;
        if (!userDept || normalizedUserDept !== normalizedDept) {
          throw new Error(`Invalid department for this admin user. Your department is: ${currentUserProfile.department}`);
        }
      }

      // Store user data in localStorage for navigation purposes only
      localStorage.setItem('role', normalizedRole);
      localStorage.setItem('dept', normalizedDept);
      localStorage.setItem('email', email);
      localStorage.setItem('userId', result.user.uid);
      
      if (shouldRecordPunchingTime(normalizedRole)) {
        // Skip punching time functionality if using Firebase Functions
        if (API_CONFIG.BASE_URL.includes('api-aovv3tvopa-uc.a.run.app')) {
          console.log('🔇 Punching time features disabled for Firebase Functions deployment');
        } else {
          await recordPunchIn({
            email: email,
            displayName: result.user.displayName,
            name: currentUserProfile?.name,
            idToken: result.idToken,
            accessToken: result.user.accessToken
          }, normalizedRole, normalizedDept);
        }
      }
      
      setSuccessMessage('Login successful! Redirecting...');
      setShowButterfly(true);
      
      let targetRoute = '/dashboard';
      
      console.log('🔍 Route Selection Debug:', {
        normalizedRole: normalizedRole,
        normalizedDept: normalizedDept,
        currentUserProfile: currentUserProfile
      });
      
      if (normalizedRole === 'superadmin' || normalizedRole === 'super_admin') {
        targetRoute = '/super-admin';
        console.log('🔍 Route: Super Admin');
      } else if (normalizedRole === 'admin' || normalizedRole === 'purchaseadmin' || normalizedRole === 'purchase_admin') {
        console.log('🔍 Route: Admin/Purchase Admin detected');
        switch (normalizedDept) {
          case 'accounts':
            targetRoute = '/admin';
            console.log('🔍 Route: Accounts Admin');
            break;
          case 'hr':
            targetRoute = '/hr-admin';
            console.log('🔍 Route: HR Admin');
            break;
          case 'marketing':
            targetRoute = '/marketing-admin';
            console.log('🔍 Route: Marketing Admin');
            break;
          case 'digital-marketing':
            targetRoute = '/digital-marketing-admin';
            console.log('🔍 Route: Digital Marketing Admin');
            break;
          case 'purchase':
          case 'purchasing':
            targetRoute = '/purchasing-admin';
            console.log('🔍 Route: Purchasing Admin');
            break;
          default:
            targetRoute = '/dashboard';
            console.log('🔍 Route: Dashboard (default for admin)');
        }
      } else {
        targetRoute = '/dashboard';
        console.log('🔍 Route: Dashboard (staff/default)');
      }
      
      console.log('🔍 Final target route:', targetRoute);
      
      setTimeout(() => {
        setShowButterfly(false);
        navigate(targetRoute);
      }, 1000);

    } catch (error) {
      let errorMessage = 'Login failed. Please try again.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/Password sign-in is disabled. Enable it in Firebase Console → Authentication → Sign-in method.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid credentials. If you recently reset your password, please wait a few minutes and try again.';
          break;
        case 'auth/user-token-expired':
          errorMessage = 'Your session has expired. Please log in again.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.';
          break;
        default:
          if (error.message && error.message.includes('password')) {
            errorMessage = 'Password authentication failed. If you recently reset your password, please wait a few minutes before trying again.';
          } else {
            errorMessage = error.message || 'Login failed. Please try again.';
          }
      }
      
      setErrors([{ msg: errorMessage }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    setErrors([]);
    setSuccessMessage('');

    try {
      const result = await signInWithGoogle();
      
      localStorage.setItem('email', result.user.email);
      localStorage.setItem('userId', result.user.uid);
      
      const defaultRole = 'user';
      localStorage.setItem('role', defaultRole);

      await recordPunchIn({
        email: result.user.email,
        displayName: result.user.displayName,
        name: result.user.displayName,
        idToken: result.idToken,
        accessToken: result.user.accessToken
      }, defaultRole, '');

      setSuccessMessage('Google login successful! Redirecting...');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error) {
      console.error('Google sign-in error:', error);
      setErrors([{ msg: 'Google sign-in failed. Please try again.' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleChange = (e) => {
    const nextRole = (e.target.value || '').toLowerCase().replace(/[ _]/g, '');
    setRole(nextRole);
    setDepartment('');
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const recordPunchIn = async (userData, role, department) => {
    try {
      const punchInData = {
        email: userData.email,
        name: userData.displayName || userData.name || 'Unknown User',
        role: role,
        department: department
      };

      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/punching-times/record-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userData.idToken || userData.accessToken}`
          },
          body: JSON.stringify(punchInData)
        });

        if (response.ok) {
          console.log('🔇 Punch-in recorded via API (silently)');
        } else if (response.status === 404 || response.status === 400) {
          console.log('🔇 Punch-in API not available (404/400), using local storage');
          throw new Error('API punch-in not available');
        } else {
          throw new Error('API punch-in failed');
        }
      } catch (apiError) {
        // Silently store in localStorage as fallback
        console.log('🔇 Storing punch-in locally');
        const localPunchIn = {
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...punchInData,
          punchOut: null,
          createdAt: new Date().toISOString(),
          isLocal: true
        };

        const existingTimes = JSON.parse(localStorage.getItem('punchingTimes') || '[]');
        existingTimes.push(localPunchIn);
        localStorage.setItem('punchingTimes', JSON.stringify(existingTimes));
      }
    } catch (error) {
      // Completely silent - no console error
      console.log('🔇 Punch-in feature unavailable, continuing with login');
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
        <img src={logo} alt="Logo" className="logo" />     
             <h2>Welcome Back</h2>
          <h3>Sign in to your account</h3>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {errors.length > 0 && (
            <div className="error-container">
              {errors.map((error, index) => (
                <div key={index} className="error-message">
                  {error.msg}
                </div>
              ))}
            </div>
          )}

          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter your email"
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="role">Role</label>
            <div className="custom-role-dropdown">
              <select
                id="role"
                className="login-role-select"
                value={role}
                onChange={handleRoleChange}
                required
              >
                <option value="">Select Role</option>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Super Admin</option>
              </select>
            </div>
          </div>

          {role === 'admin' && (
            <div className="form-group">
              <label htmlFor="department">Department</label>
              <select
                id="department"
                value={dept}
                onChange={(e) => setDepartment(e.target.value)}
                required
              >
                <option value="">Select Department</option>
                <option value="hr">HR</option>
                <option value="marketing">Marketing</option>
                <option value="accounts">Accounts</option>
                <option value="purchase">Purchase</option>
                <option value="digital-marketing">Digital Marketing</option>
              </select>
            </div>
          )}

          <button
            type="submit"
            className={`login-btn ${isSubmitting ? 'loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          <button
            type="button"
            className="google-btn"
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
          >
            <FaGoogle />
            Sign in with Google
          </button>

          <div className="login-links">
            <Link to="/forgot-password" className="forgot-password">
              Forgot Password?
            </Link>
            <Link to="/register" className="register-link">
              Don't have an account? Sign up
            </Link>
          </div>
        </form>
      </div>

      {showButterfly && (
        <img
          src="https://cdn.pixabay.com/animation/2023/03/05/21/13/21-13-51-872_512.gif"
          alt="Butterfly Animation"
          className="butterfly-gif-animation"
          style={{
            position: 'absolute',
            top: '30%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            zIndex: 1000,
            pointerEvents: 'none',
            animation: 'fly 1.5s linear'
          }}
        />
      )}
    </div>
  );
};

export default Login;